/*
 * Created on Sat Apr 03 2021
 *
 * Copyright (c) 2021 Coding House LLC
 */
import { CommunityPost } from "../models/CommunityPost";
import {
    AccessModel,
    ContentEntityType,
    ContentReadResponse,
    ContentResponse,
    EntityAttachment,
    EntityReference,
    isContentReadResponse,
    TopicTagRef,
} from "../models/content";
import { Exercise } from "../models/Exercise";
import { Workout } from "../models/Workout";
import { BuilderEntitySearchResult } from "../screens/create/types";
import { getLocaleCode } from "../utils/Utilities";
import { StrongFoundationService } from "./StrongFoundationService";
import { DateTime } from "luxon";

type LinkableSearchResult = {
    entityType: string;
    entityId: string;
    displayName: string;
    subtitle?: string;
    imageUrl?: string;
    canMention: boolean;
};

export class CommunityService extends StrongFoundationService {
    public constructor() {
        super();
    }

    public incrementViewCount = async (id: string): Promise<void> => {
        try {
            await this.post(`community/post/${id}/upvotes/increment`);
        } catch (ex) {
            // no op
        }
    };

    public incrementUpvote = async (id: string): Promise<void> => {
        try {
            await this.post(`community/post/${id}/upvotes/increment`);
        } catch {
            throw new Error("Failed to increment upvote");
        }
    };

    public decrementUpvote = async (id: string): Promise<void> => {
        try {
            await this.post(`community/post/${id}/upvotes/decrement`);
        } catch {
            throw new Error("Failed to decrement upvote");
        }
    };

    public fetchPostResponse = async (id: string): Promise<ContentResponse<CommunityPost> | undefined> => {
        try {
            return await this.get<ContentResponse<CommunityPost>>(`community/post/${id}`, {
                language: getLocaleCode(),
            });
        } catch (ex) {
            return undefined;
        }
    };

    public fetchPost = async (id: string): Promise<CommunityPost | undefined> => {
        try {
            const response = await this.fetchPostResponse(id);
            if (!response) {
                return undefined;
            }

            if (isContentReadResponse<CommunityPost>(response)) {
                return response.data;
            }

            return response;
        } catch (ex) {
            return undefined;
        }
    };
    public fetchPosts = async (afterDate: number, skip: number, isPublished = true): Promise<CommunityPost[]> => {
        try {
            const response = await this.get<CommunityPost[]>("community", {
                afterDate,
                skip,
                isPublished,
                language: getLocaleCode(),
            });
            return response ?? [];
        } catch (ex) {
            return [];
        }
    };

    public fetchUserPosts = async (
        userId: string,
        isOwner: boolean,
        afterDate = DateTime.utc().toMillis(),
        skip = 0,
    ): Promise<CommunityPost[]> => {
        try {
            const response = await this.get<CommunityPost[]>(`community/user/${userId}`, {
                afterDate,
                skip,
                language: getLocaleCode(),
            });
            return response ?? [];
        } catch {
            return [];
        }
    };

    public createPost = async (input: {
        title: string;
        content: string;
        headerImage: string;
        authorId: string;
        attachments: EntityAttachment[];
        topicTags: TopicTagRef[];
        entityRefs: EntityReference[];
        accessModel: AccessModel;
    }): Promise<CommunityPost | undefined> => {
        const body = {
            post: {
                ...input,
                isPublished: true,
                dateCreated: DateTime.utc().toMillis(),
                language: getLocaleCode(),
            },
        };
        return this.post<CommunityPost>("community", body);
    };

    public uploadPostHeaderImage = async (postId: string, file: File): Promise<CommunityPost | undefined> => {
        const formData = new FormData();
        formData.append("headerImage", file);
        return this.postFormData<CommunityPost>(`community/image/${postId}`, formData);
    };

    public searchPostsByName = async (query: string): Promise<BuilderEntitySearchResult[]> => {
        if (!query.trim()) {
            return [];
        }
        try {
            const response = await this.get<CommunityPost[]>("community/search", {
                q: query,
                language: getLocaleCode(),
            });
            return (response ?? []).map((post) => ({
                entityId: String(post._id ?? ""),
                entityType: "post",
                displayName: post.title || "Post",
                subtitle: post.author?.username ? `@${post.author.username}` : "Post",
                imageUrl: post.headerImage,
            })).filter((item) => !!item.entityId);
        } catch {
            return [];
        }
    };

    public searchCommunityPosts = async (query: string): Promise<CommunityPost[]> => {
        if (!query.trim()) {
            return [];
        }

        try {
            const response = await this.get<CommunityPost[]>("community/search", {
                q: query,
                language: getLocaleCode(),
            });
            return response ?? [];
        } catch {
            return [];
        }
    };

    public searchWorkoutAttachmentCandidates = async (query: string): Promise<BuilderEntitySearchResult[]> => {
        if (!query.trim()) {
            return [];
        }
        try {
            const response = await this.get<CommunityPost[]>("community/search", {
                q: query,
                language: getLocaleCode(),
            });
            const mapped = (response ?? []).flatMap((post) => {
                const workoutAttachment = (post.attachments ?? []).find((attachment) => attachment.entityType === ContentEntityType.Workout);
                if (!workoutAttachment?.entityId) {
                    return [];
                }
                return [{
                    entityId: workoutAttachment.entityId,
                    entityType: "workout",
                    displayName: post.title || "Workout",
                    subtitle: "Workout",
                    imageUrl: post.headerImage,
                }];
            });

            const unique = new Map<string, BuilderEntitySearchResult>();
            mapped.forEach((item) => {
                unique.set(`${item.entityType}:${item.entityId}`, item);
            });
            return Array.from(unique.values());
        } catch {
            return [];
        }
    };

    public searchLinkableEntities = async (query: string): Promise<BuilderEntitySearchResult[]> => {
        if (!query.trim()) {
            return [];
        }

        try {
            const response = await this.get<{ results: LinkableSearchResult[] }>("content/linkable-search", {
                q: query.trim(),
            });

            return (response?.results ?? [])
                .filter((result) => result.canMention)
                .map((result) => ({
                    entityId: result.entityId,
                    entityType: result.entityType,
                    displayName: result.displayName,
                    subtitle: result.subtitle,
                    imageUrl: result.imageUrl,
                }));
        } catch {
            return [];
        }
    };

    public buildWorkout = async (
        workout: Workout,
        phoneNumbers: string[] | undefined = undefined,
    ): Promise<string | undefined | null> => {
        try {
            (workout as any).language = getLocaleCode();
            (workout as any).sqlId = workout.id;
            const workoutSaved = await this.post<Workout & { _id: string }>("exercise/workout", {
                workout
            });
            this.post<Workout & { _id: string }>(`exercise/workout/${workoutSaved?._id}/share`, {
                phoneNumbers,
                language: getLocaleCode(),
            });
            return workoutSaved?._id;
        } catch (ex) {
            return undefined;
        }
    };

    public getWorkoutResponse = async (id: string): Promise<ContentReadResponse<Workout> | undefined> => {
        try {
            const response = await this.get<ContentReadResponse<Workout>>(`exercise/workout/${id}`, {
                language: getLocaleCode(),
            });
            return response;
        } catch (ex) {
            return undefined;
        }
    };

    public getWorkoutExercises = async (id: string): Promise<{ workout: Workout; exercises: Exercise[] } | undefined> => {
        try {
            const data = await this.get<{ workout: Workout; exercises: Exercise[] }>(
                `exercise/workout/${id}/exercise`,
                {
                    language: getLocaleCode(),
                },
            );
            return data;
        } catch (ex) {
            return undefined;
        }
    };

    public getWorkout = async (id: string): Promise<{ workout: Workout; exercises: Exercise[] } | undefined> => {
        return this.getWorkoutExercises(id);
    };
}

export const communityService = new CommunityService();
